<template>
  <div class="min-h-screen flex flex-col bg-gray-50">
    <!-- Header -->
    <header>
      <img
        src="https://vlecad.be/wp-content/uploads/2025/01/Logo-Horizontaal-registered.png"
        alt="Vlecad Logo"
      />
      <div class="header-text">
        <h1 class="text-2xl font-bold">E-Learning</h1>
      </div>

      <!-- Search Field in Header -->
      <div class="mt-4 w-full max-w-md">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Zoek cursussen..."
          class="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <!-- Button linking to AutoFIT MNC -->
      <div class="mt-4 max-w-md" style="margin-left: 20px">
        <a href="https://my.vlecad.be/Autofit_MNC" target="_blank">
          <button
            class="bg-blue-900 text-white py-2 px-4 rounded hover:bg-blue-800 transition"
          >
            Start proef
          </button>
        </a>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto flex-grow px-4 py-8">
      <div class="mb-8 text-center">
        <h2 class="text-4xl font-extrabold text-gray-800 mb-4">
          Ontdek Cursussen
        </h2>
        <p class="text-lg text-gray-600">
          Ontdek ons aanbod aan zelfstudiecursussen en vergroot je kennis op
          jouw eigen tempo.
        </p>
      </div>

      <!-- Loading / Error Messages -->
      <div v-if="loading" class="text-center text-gray-600 mb-6">
        Cursussen laden...
      </div>
      <div v-if="error" class="text-center text-red-500 mb-6">
        {{ error }}
      </div>

      <!-- Courses Grid -->
      <div class="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="group in filteredCourses"
          :key="group.name"
          class="bg-white p-4 rounded-lg shadow"
        >
          <h2 class="text-2xl font-bold mb-4 text-blue-900">
            {{ group.name }}
          </h2>

          <div
            v-for="course in group.courses"
            :key="course.id"
            @click="selectCourse(course)"
            class="cursor-pointer bg-gray-100 rounded-lg shadow hover:shadow-lg transition p-4 flex flex-col"
          >
            <img
              :src="course.logo.replace(/\\\\/g, '')"
              alt="Cursus Logo"
              class="w-full h-40 object-cover rounded-md mb-4"
            />
            <h3 class="text-xl font-semibold text-gray-800 mb-2">
              {{ course.name }}
            </h3>
            <p class="text-gray-600 flex-grow">{{ course.description }}</p>
            <button
              class="mt-4 bg-blue-900 text-white py-2 rounded hover:bg-blue-800 transition"
            >
              Bekijk Cursus
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="text-white py-4">
      <div class="container mx-auto px-4 text-center">
        <p>&copy; {{ new Date().getFullYear() }} Vlecad. Alle rechten voorbehouden.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "App",
  setup() {
    const courses = ref([]);
    const searchQuery = ref("");
    const loading = ref(true);
    const error = ref(null);

    const userEmail = ref("");

    const fetchCourses = async () => {
      try {
        const response = await axios.get(
          "https://my.vlecad.be/api/course/read0.php"
        );
        courses.value = response.data.courses || [];
      } catch (err) {
        error.value = "Cursussen ophalen is mislukt";
      } finally {
        loading.value = false;
      }
    };

    const filteredCourses = computed(() => {
      let filtered = courses.value.filter((course) =>
        course.name.toLowerCase().includes(searchQuery.value.toLowerCase())
      );

      let groupedCourses = {};

      filtered.forEach((course) => {
        let groupKey = course.name;

        if (course.name.toLowerCase().includes("korpus")) {
          groupKey = "Korpus";
        } else if (course.name.toLowerCase().includes("autofit")) {
          groupKey = "AutoFIT";
        }

        if (!groupedCourses[groupKey]) {
          groupedCourses[groupKey] = [];
        }
        groupedCourses[groupKey].push(course);
      });

      return Object.entries(groupedCourses).map(([name, courses]) => ({
        name,
        courses,
      }));
    });

    const initializeUser = () => {
      let storedEmail = localStorage.getItem("userEmail");

      if (!storedEmail) {
        storedEmail = `${uuidv4()}@example.com`;
        localStorage.setItem("userEmail", storedEmail);
      }

      userEmail.value = storedEmail;
    };

    const selectCourse = (course) => {
      const courseUrl = `https://my.vlecad.be/api/uploads/courses/${course.url}/story.html?actor=%7B%22mbox%22%3A%22mailto%3A${encodeURIComponent(
        userEmail.value
      )}%22%2C%22objectType%22%3A%22Agent%22%7D`;

      window.open(courseUrl, "_blank");
    };

    onMounted(() => {
      initializeUser();
      fetchCourses();
    });

    return {
      courses,
      searchQuery,
      filteredCourses,
      loading,
      error,
      selectCourse,
    };
  },
};
</script>
